<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { usePasswordReset } from '@/composables/usePasswordReset';
import { mdiAlertCircleOutline, mdiEmailOutline, mdiCheckCircleOutline, mdiInformationOutline } from '@mdi/js';

// Types
interface FormData {
  email: string;
}

// Define emits
const emit = defineEmits<{
  'switch-to-login': []
}>();

// Composables
const router = useRouter();
const { loading, successMessage, errorMessage, requestPasswordReset, resetMessages } = usePasswordReset();

// Reactive state
const form = ref();
const formValid = ref(false); // For Vuetify form validation
const formData = ref<FormData>({
  email: ''
});
const showForm = ref(true);
const countdown = ref(0);
const redirectTimer = ref<NodeJS.Timeout | null>(null);

// Validation rules
const emailRules = [
  (v: string) => !!v || 'E-mail is required',
  (v: string) => /.+@.+\..+/.test(v) || 'E-mail must be valid'
];

// Computed
const isFormValid = computed(() => {
  return formData.value.email && /.+@.+\..+/.test(formData.value.email);
});

// Methods
const resetForm = () => {
  formData.value.email = '';
  form.value?.reset();
};

const startCountdown = () => {
  countdown.value = 3;
  showForm.value = false;
  
  const countdownInterval = setInterval(() => {
    countdown.value--;
    console.log('Countdown:', countdown.value); // Debug log
    
    if (countdown.value <= 0) {
      clearInterval(countdownInterval);
      console.log('Switching to login form...'); // Debug log
      
      // Emit event to parent component to switch to login form
      emit('switch-to-login');
    }
  }, 1000);
};

const handleSubmit = async () => {
  if (!isFormValid.value) return;
  
  const success = await requestPasswordReset(formData.value.email);
  
  if (success) {
    resetForm();
    // Start countdown timer and hide form
    startCountdown();
  }
};
</script>

<template>
  <!-- Show form when showForm is true -->
  <v-form 
    v-if="showForm"
    ref="form" 
    v-model="formValid"
    @submit.prevent="handleSubmit" 
    class="login-form"
  >
    <p class="form-description mb-6">
      Enter your email address below and we'll send you password reset instructions.
    </p>
    
    <v-alert
      v-if="errorMessage"
      color="error"
      variant="tonal"
      density="compact"
      :icon="mdiAlertCircleOutline"
      class="mb-4"
    >
      {{ errorMessage }}
    </v-alert>
    
    <v-text-field
      v-model="formData.email"
      :rules="emailRules"
      label="Email"
      type="email"
      required
      density="comfortable"
      variant="outlined"
      hide-details="auto"
      class="input-field mb-4"
      :disabled="loading"
      :prepend-inner-icon="mdiEmailOutline"
      autocomplete="email"
    />

    <v-btn 
      block 
      class="sign-in-btn" 
      type="submit"
      :loading="loading"
      :disabled="!isFormValid || loading"
      color="primary"
    >
      Reset Password
    </v-btn>
  </v-form>

  <!-- Show success message and countdown when form is hidden -->
  <div v-else class="success-container text-center">
    <v-icon 
      size="64" 
      color="success" 
      class="mb-4"
      :icon="mdiCheckCircleOutline"
    />
    
    <h3 class="text-h5 mb-4 text-success">
      Email Sent Successfully!
    </h3>
    
    <p class="text-body-1 mb-6 text-medium-emphasis">
      {{ successMessage }}
    </p>
    
    <v-alert
      color="info"
      variant="tonal"
      density="compact"
      :icon="mdiInformationOutline"
      class="mb-4"
    >
      <span v-if="countdown > 0">
        Switching to Sign In in {{ countdown }} second{{ countdown !== 1 ? 's' : '' }}...
      </span>
      <span v-else>
        Switching to Sign In...
      </span>
    </v-alert>
    
    <v-btn 
      color="primary" 
      variant="outlined"
      @click="emit('switch-to-login')"
    >
      Go to Login Now
    </v-btn>
  </div>
</template>

<style lang="scss" scoped>
.form-description {
  color: #64748b;
  font-size: 0.95rem;
  line-height: 1.5;
}

.input-field {
  margin-bottom: 1rem;
}

.success-container {
  padding: 2rem 0;
  
  .v-icon {
    opacity: 0.8;
  }
}
</style>
